import { useState } from "react";

export interface SavedSearch {
  id: string;
  name: string;
  description?: string;
  stage: string;
  filters: string;
  createdAt: string;
}

const STORAGE_KEY = "lusca-search-find-transactions-views";

interface UseSavedSearchesReturn {
  searches: SavedSearch[];
  saveSearch: (
    name: string,
    description: string,
    stage: string,
    filters: string
  ) => void;
  deleteSearch: (id: string) => void;
  renameSearch: (id: string, newName: string) => void;
}

export default function useSavedSearches(): UseSavedSearchesReturn {
  const [searches, setSearches] = useState<SavedSearch[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  const saveToStorage = (updated: SavedSearch[]) => {
    setSearches(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  };

  const isNameTaken = (name: string, excludeId?: string) =>
    searches.some(
      (s) => s.name.toLowerCase() === name.toLowerCase() && s.id !== excludeId
    );

  const saveSearch = (
    name: string,
    description: string,
    stage: string,
    filters: string
  ): void => {
    if (isNameTaken(name)) {
      throw new Error("A saved search with this name already exists");
    }
    const newSearch: SavedSearch = {
      id: crypto.randomUUID(),
      name,
      description: description || undefined,
      stage,
      filters,
      createdAt: new Date().toISOString(),
    };
    saveToStorage([...searches, newSearch]);
  };

  const deleteSearch = (id: string) => {
    saveToStorage(searches.filter((s) => s.id !== id));
  };

  const renameSearch = (id: string, newName: string): void => {
    if (isNameTaken(newName, id)) {
      throw new Error("A saved search with this name already exists");
    }
    saveToStorage(
      searches.map((s) => (s.id === id ? { ...s, name: newName } : s))
    );
  };

  return { searches, saveSearch, deleteSearch, renameSearch };
}
